import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Form, Modal, Spinner, Alert, Badge, Table, Tabs, Tab } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { colors } from '../../brand-colors';

/**
 * Command Center version of Product Catalog
 * Simplified view focused on managing products within Command Center context
 */
function CCProductCatalog() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Edit/Add modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [isAddMode, setIsAddMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('basics');
  const [generatingUSP, setGeneratingUSP] = useState(false);

  // Features/Benefits management
  const [newFeature, setNewFeature] = useState('');
  const [newBenefit, setNewBenefit] = useState('');
  const [newFBType, setNewFBType] = useState('feature');
  const [editingFBIndex, setEditingFBIndex] = useState(null);

  // Image upload
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await fetch('/api/products/get-catalog', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setProducts(data.catalog || []);
      }
    } catch (err) {
      setError('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = () => {
    setEditingProduct({
      name: '',
      position: '',
      sale_price: '',
      cost_to_deliver: '',
      profit_per_sale: '',
      purchase_type: 'one-time',
      monthly_churn_rate: '',
      notes: '',
      is_primary_tripwire: false,
      type: 'digital',
      features_benefits: []
    });
    setIsAddMode(true);
    setActiveTab('basics');
    setShowEditModal(true);
  };

  const openEditModal = (product) => {
    // Parse features_benefits if it's a JSON string
    let parsedProduct = { ...product };
    if (product.features_benefits && typeof product.features_benefits === 'string') {
      try {
        parsedProduct.features_benefits = JSON.parse(product.features_benefits);
      } catch {
        parsedProduct.features_benefits = [];
      }
    }
    if (!parsedProduct.features_benefits) {
      parsedProduct.features_benefits = [];
    }
    setEditingProduct(parsedProduct);
    setIsAddMode(false);
    setActiveTab('basics');
    setShowEditModal(true);
  };

  const addFeatureBenefit = () => {
    if (!newFeature.trim()) return;
    const currentFeatures = [...(editingProduct.features_benefits || [])];
    const entry = { feature: newFBType === 'feature' ? newFeature.trim() : '', benefit: newFBType === 'benefit' ? newFeature.trim() : '', type: newFBType };
    if (editingFBIndex !== null) {
      currentFeatures[editingFBIndex] = entry;
    } else {
      currentFeatures.push(entry);
    }
    setEditingProduct({ ...editingProduct, features_benefits: currentFeatures });
    setNewFeature('');
    setNewBenefit('');
    setNewFBType('feature');
    setEditingFBIndex(null);
  };

  const startEditFeatureBenefit = (index) => {
    const fb = (editingProduct.features_benefits || [])[index];
    if (!fb) return;
    setNewFeature(fb.feature || fb.benefit || '');
    setNewFBType(fb.type || (fb.benefit && !fb.feature ? 'benefit' : 'feature'));
    setEditingFBIndex(index);
  };

  const removeFeatureBenefit = (index) => {
    const currentFeatures = editingProduct.features_benefits || [];
    setEditingProduct({
      ...editingProduct,
      features_benefits: currentFeatures.filter((_, i) => i !== index)
    });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Please upload a PNG, JPG, or WebP image');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be less than 5MB');
      return;
    }

    // Need a saved product ID to upload
    if (!editingProduct.id) {
      setError('Please save the product first before uploading an image');
      return;
    }

    setUploadingImage(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('image', file);

      const response = await fetch(`/api/products/upload-image/${editingProduct.id}`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setEditingProduct({ ...editingProduct, image_path: data.image_url });
        // Update in products list too
        setProducts(products.map(p =>
          p.id === editingProduct.id ? { ...p, image_path: data.image_url } : p
        ));
        setSuccess('Image uploaded successfully');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to upload image');
      }
    } catch (err) {
      setError('Error uploading image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleDeleteImage = async () => {
    if (!editingProduct.id) return;

    try {
      const response = await fetch(`/api/products/delete-image/${editingProduct.id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        setEditingProduct({ ...editingProduct, image_path: null });
        setProducts(products.map(p =>
          p.id === editingProduct.id ? { ...p, image_path: null } : p
        ));
        setSuccess('Image removed');
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      setError('Error removing image');
    }
  };

  const generateUSP = async () => {
    setGeneratingUSP(true);
    try {
      const response = await fetch('/api/usp/generate-for-product', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          product_id: editingProduct.id,
          product_name: editingProduct.name,
          customer_problem: editingProduct.usp_customer_problem,
          distinct_advantage: editingProduct.usp_distinct_advantage,
          benefits: editingProduct.usp_benefits,
          promise: editingProduct.usp_promise
        })
      });

      if (response.ok) {
        const data = await response.json();
        setEditingProduct({
          ...editingProduct,
          usp: data.usp
        });
      } else {
        setError('Failed to generate USP');
      }
    } catch (err) {
      setError('Error generating USP');
    } finally {
      setGeneratingUSP(false);
    }
  };

  const saveProduct = async () => {
    // Validate required fields
    if (!editingProduct.name?.trim()) {
      setError('Product name is required');
      return;
    }

    setSaving(true);
    try {
      // Serialize features_benefits to JSON string for storage
      const productToSave = {
        ...editingProduct,
        features_benefits: JSON.stringify(editingProduct.features_benefits || [])
      };

      let updatedProducts;
      if (isAddMode) {
        // Add new product to the list
        updatedProducts = [...products, productToSave];
      } else {
        // Update existing product
        updatedProducts = products.map(p =>
          p.id === editingProduct.id ? productToSave : p
        );
      }

      const response = await fetch('/api/products/save-catalog', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          catalog: updatedProducts,
          all_products: updatedProducts
        })
      });

      if (response.ok) {
        // Refresh products from server to get the new ID for added products
        if (isAddMode) {
          await fetchProducts();
        } else {
          setProducts(updatedProducts);
        }
        setShowEditModal(false);
        setSuccess(isAddMode ? 'Product added successfully' : 'Product updated successfully');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to save product');
      }
    } catch (err) {
      setError('Error saving product');
    } finally {
      setSaving(false);
    }
  };

  const getPositionBadge = (position) => {
    const badges = {
      'entry': { bg: 'success', label: '🎣 Entry/Tripwire' },
      'tripwire': { bg: 'success', label: '🎣 Entry/Tripwire' }, // legacy alias
      'core': { bg: 'primary', label: '⭐ Core Offer' },
      'upsell': { bg: 'info', label: '⬆️ Upsell' },
      'cross-sell': { bg: 'secondary', label: '↔️ Cross-sell' },
      'premium': { bg: 'warning', label: '💎 Premium' },
      'downsell': { bg: 'light', label: '⬇️ Downsell' },
      'backend': { bg: 'dark', label: 'Backend' } // legacy
    };
    const badge = badges[position] || { bg: 'secondary', label: position || 'Unassigned' };
    return <Badge bg={badge.bg}>{badge.label}</Badge>;
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" style={{ color: colors.goldMid }} />
        <p className="mt-3">Loading products...</p>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <Button
            variant="link"
            className="p-0 mb-2"
            onClick={() => navigate('/command-center/products')}
          >
            ← Back to Products
          </Button>
          <h1 className="mb-1">Product Catalog</h1>
          <p className="text-muted mb-0">
            Manage your products, pricing, and value ladder positioning.
          </p>
        </div>
        <div className="d-flex gap-2">
          <Button
            variant="success"
            onClick={openAddModal}
          >
            + Add Product
          </Button>
          <Button
            variant="outline-primary"
            onClick={() => navigate('/command-center/products/upgrade-paths')}
            title="Define how customers move up your product ladder — from entry-level to premium"
          >
            Upgrade Paths →
          </Button>
        </div>
      </div>

      {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}

      {/* Products Table */}
      {products.length === 0 ? (
        <Card className="shadow-sm">
          <Card.Body className="text-center py-5">
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📦</div>
            <h4>No Products Yet</h4>
            <p className="text-muted mb-4">
              Add products to your catalog to start building your value ladder.
            </p>
            <Button
              variant="success"
              size="lg"
              onClick={openAddModal}
            >
              + Add Your First Product
            </Button>
          </Card.Body>
        </Card>
      ) : (
        <Card className="shadow-sm">
          <Table responsive hover className="mb-0">
            <thead style={{ backgroundColor: colors.greyLightest }}>
              <tr>
                <th>Product</th>
                <th>Position</th>
                <th>Type</th>
                <th className="text-end">Price</th>
                <th className="text-end">Profit</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.map(product => (
                <tr key={product.id}>
                  <td>
                    <div className="d-flex align-items-center gap-2">
                      {product.image_path && (
                        <img
                          src={product.image_path}
                          alt=""
                          style={{
                            width: '40px',
                            height: '40px',
                            objectFit: 'cover',
                            borderRadius: '4px'
                          }}
                        />
                      )}
                      <div>
                        <strong>{product.name}</strong>
                        {product.is_primary_tripwire && (
                          <Badge bg="success" className="ms-2">Primary Tripwire</Badge>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>{getPositionBadge(product.position)}</td>
                  <td>
                    <span className="text-muted">
                      {product.purchase_type?.includes('recurring') ? 'Recurring' : 'One-time'}
                    </span>
                  </td>
                  <td className="text-end">${product.sale_price?.toLocaleString() || '0'}</td>
                  <td className="text-end text-success">${product.profit_per_sale?.toLocaleString() || '0'}</td>
                  <td className="text-center">
                    <Button
                      variant="link"
                      size="sm"
                      onClick={() => openEditModal(product)}
                    >
                      Edit
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      )}

      {/* Quick Stats */}
      {products.length > 0 && (
        <Row className="mt-4">
          <Col md={6}>
            <Card className="text-center border-0 shadow-sm">
              <Card.Body>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: colors.goldMid }}>
                  {products.length}
                </div>
                <div className="text-muted">Total Products</div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={6}>
            <Card className="text-center border-0 shadow-sm">
              <Card.Body>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#6f42c1' }}>
                  {products.filter(p => p.purchase_type?.includes('recurring')).length}
                </div>
                <div className="text-muted">Recurring Products</div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Add/Edit Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
        <Modal.Header closeButton style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
          <Modal.Title>{isAddMode ? 'Add New Product' : 'Edit Product'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editingProduct && (
            <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-3">
              {/* BASICS TAB */}
              <Tab eventKey="basics" title="Basics">
                <Form>
                  <Row>
                    <Col md={8}>
                      <Form.Group className="mb-3">
                        <Form.Label><strong>Product Name</strong></Form.Label>
                        <Form.Control
                          type="text"
                          value={editingProduct.name || ''}
                          onChange={(e) => setEditingProduct({ ...editingProduct, name: e.target.value })}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label><strong>Position</strong></Form.Label>
                        <Form.Select
                          value={editingProduct.position || ''}
                          onChange={(e) => setEditingProduct({ ...editingProduct, position: e.target.value })}
                        >
                          <option value="">Select...</option>
                          <option value="entry">🎣 Entry/Tripwire</option>
                          <option value="core">⭐ Core Offer</option>
                          <option value="upsell">⬆️ Upsell</option>
                          <option value="cross-sell">↔️ Cross-sell</option>
                          <option value="premium">💎 Premium</option>
                          <option value="downsell">⬇️ Downsell</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label><strong>Sale Price ($)</strong></Form.Label>
                        <Form.Control
                          type="number"
                          value={editingProduct.sale_price || ''}
                          onChange={(e) => setEditingProduct({ ...editingProduct, sale_price: parseFloat(e.target.value) || 0 })}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label><strong>Cost to Deliver ($)</strong></Form.Label>
                        <Form.Control
                          type="number"
                          value={editingProduct.cost_to_deliver || ''}
                          onChange={(e) => setEditingProduct({ ...editingProduct, cost_to_deliver: parseFloat(e.target.value) || 0 })}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label><strong>Profit per Sale ($)</strong></Form.Label>
                        <Form.Control
                          type="number"
                          value={editingProduct.profit_per_sale || ''}
                          onChange={(e) => setEditingProduct({ ...editingProduct, profit_per_sale: parseFloat(e.target.value) || 0 })}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label><strong>Product Type</strong></Form.Label>
                        <Form.Select
                          value={editingProduct.type || 'digital'}
                          onChange={(e) => setEditingProduct({ ...editingProduct, type: e.target.value })}
                        >
                          <option value="digital">Digital Product</option>
                          <option value="physical">Physical Product</option>
                          <option value="service">Service</option>
                          <option value="coaching">Coaching/Consulting</option>
                          <option value="membership">Membership</option>
                          <option value="course">Course</option>
                          <option value="software">Software/SaaS</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label><strong>Purchase Type</strong></Form.Label>
                        <Form.Select
                          value={editingProduct.purchase_type || 'one-time'}
                          onChange={(e) => setEditingProduct({ ...editingProduct, purchase_type: e.target.value })}
                        >
                          <option value="one-time">One-time</option>
                          <option value="recurring">Recurring</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      {editingProduct.purchase_type?.includes('recurring') && (
                        <Form.Group className="mb-3">
                          <Form.Label><strong>Monthly Churn (%)</strong></Form.Label>
                          <Form.Control
                            type="number"
                            step="0.1"
                            value={editingProduct.monthly_churn_rate || ''}
                            onChange={(e) => setEditingProduct({ ...editingProduct, monthly_churn_rate: parseFloat(e.target.value) || 0 })}
                          />
                        </Form.Group>
                      )}
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label><strong>Notes</strong></Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={editingProduct.notes || ''}
                      onChange={(e) => setEditingProduct({ ...editingProduct, notes: e.target.value })}
                    />
                  </Form.Group>

                  {/* Product Image Upload */}
                  <Form.Group className="mb-3">
                    <Form.Label><strong>Product Image</strong></Form.Label>
                    {editingProduct.image_path ? (
                      <div className="d-flex align-items-start gap-3">
                        <img
                          src={editingProduct.image_path}
                          alt="Product"
                          style={{
                            maxWidth: '150px',
                            maxHeight: '150px',
                            objectFit: 'cover',
                            borderRadius: '8px',
                            border: '1px solid #dee2e6'
                          }}
                        />
                        <div>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={handleDeleteImage}
                          >
                            Remove Image
                          </Button>
                          <div className="mt-2">
                            <Form.Control
                              type="file"
                              accept="image/png,image/jpeg,image/jpg,image/webp"
                              onChange={handleImageUpload}
                              disabled={uploadingImage}
                              size="sm"
                            />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div>
                        {!isAddMode ? (
                          <>
                            <Form.Control
                              type="file"
                              accept="image/png,image/jpeg,image/jpg,image/webp"
                              onChange={handleImageUpload}
                              disabled={uploadingImage}
                            />
                            <Form.Text className="text-muted">
                              {uploadingImage ? 'Uploading...' : 'Upload a product image (PNG, JPG, WebP - max 5MB)'}
                            </Form.Text>
                          </>
                        ) : (
                          <div className="text-muted p-3 border rounded" style={{ backgroundColor: '#f8f9fa' }}>
                            Save the product first, then you can upload an image.
                          </div>
                        )}
                      </div>
                    )}
                  </Form.Group>

                  <Form.Check
                    type="checkbox"
                    label="This is my primary tripwire product"
                    checked={editingProduct.is_primary_tripwire || false}
                    onChange={(e) => setEditingProduct({ ...editingProduct, is_primary_tripwire: e.target.checked })}
                  />
                </Form>
              </Tab>

              {/* USP TAB */}
              <Tab eventKey="usp" title="USP">
                <div className="mb-3 p-3" style={{ backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                  <small className="text-muted">
                    Your Unique Selling Proposition explains why customers should choose THIS product over alternatives.
                    Answer the questions below and we'll help generate a compelling USP.
                  </small>
                </div>

                <Form.Group className="mb-3">
                  <Form.Label><strong>1. Customer Problem</strong></Form.Label>
                  <Form.Text className="d-block mb-2 text-muted">
                    What specific need, problem, or desire does this product solve? How do you solve it better than anyone else?
                  </Form.Text>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder="e.g., Busy entrepreneurs struggle to find time for marketing. We automate 80% of their social media in 15 minutes/week."
                    value={editingProduct.usp_customer_problem || ''}
                    onChange={(e) => setEditingProduct({ ...editingProduct, usp_customer_problem: e.target.value })}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label><strong>2. Distinct Advantage</strong></Form.Label>
                  <Form.Text className="d-block mb-2 text-muted">
                    What's truly different about your offering? (Quality, convenience, unique feature - NOT just price)
                  </Form.Text>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder="e.g., Only solution that uses AI trained specifically on your brand voice, not generic templates."
                    value={editingProduct.usp_distinct_advantage || ''}
                    onChange={(e) => setEditingProduct({ ...editingProduct, usp_distinct_advantage: e.target.value })}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label><strong>3. Benefits</strong></Form.Label>
                  <Form.Text className="d-block mb-2 text-muted">
                    What do customers GET from using this product? Focus on outcomes, not features.
                  </Form.Text>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder="e.g., More leads, consistent brand presence, 10+ hours saved per month, peace of mind."
                    value={editingProduct.usp_benefits || ''}
                    onChange={(e) => setEditingProduct({ ...editingProduct, usp_benefits: e.target.value })}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label><strong>4. Promise</strong></Form.Label>
                  <Form.Text className="d-block mb-2 text-muted">
                    What can you consistently deliver and back up with your actions, products, and services?
                  </Form.Text>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder="e.g., If you don't see engagement increase within 30 days, we'll refund your investment."
                    value={editingProduct.usp_promise || ''}
                    onChange={(e) => setEditingProduct({ ...editingProduct, usp_promise: e.target.value })}
                  />
                </Form.Group>

                <hr />

                <Form.Group className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <Form.Label className="mb-0"><strong>Your USP Statement</strong></Form.Label>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={generateUSP}
                      disabled={generatingUSP}
                    >
                      {generatingUSP ? <><Spinner size="sm" className="me-1" /> Generating...</> : 'AI Generate USP'}
                    </Button>
                  </div>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    placeholder="Your unique selling proposition will appear here..."
                    value={editingProduct.usp || ''}
                    onChange={(e) => setEditingProduct({ ...editingProduct, usp: e.target.value })}
                  />
                </Form.Group>
              </Tab>

              {/* FEATURES & BENEFITS TAB */}
              <Tab eventKey="features" title="Features & Benefits">
                <div className="mb-3 p-3" style={{ backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                  <small className="text-muted">
                    List specific features of this product and the benefit each provides to customers.
                    These are used in sales pages, emails, and marketing materials.
                  </small>
                </div>

                {/* Add new feature/benefit */}
                <Card className="mb-3">
                  <Card.Body>
                    <Row>
                      <Col md={7}>
                        <Form.Group>
                          <Form.Label><strong>{newFBType === 'benefit' ? 'Benefit' : 'Feature'}</strong></Form.Label>
                          <Form.Control
                            type="text"
                            placeholder={newFBType === 'benefit' ? 'e.g., Never miss a posting window again' : 'e.g., AI-powered scheduling'}
                            value={newFeature}
                            onChange={(e) => setNewFeature(e.target.value)}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label><strong>Type</strong></Form.Label>
                          <Form.Select value={newFBType} onChange={(e) => setNewFBType(e.target.value)}>
                            <option value="feature">Feature</option>
                            <option value="benefit">Benefit</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={2} className="d-flex align-items-end">
                        <Button
                          variant={editingFBIndex !== null ? 'dark' : 'success'}
                          onClick={addFeatureBenefit}
                          disabled={!newFeature.trim()}
                          className="w-100"
                        >
                          {editingFBIndex !== null ? 'Save' : 'Add'}
                        </Button>
                        {editingFBIndex !== null && (
                          <Button
                            variant="link"
                            size="sm"
                            className="w-100 mt-1"
                            onClick={() => { setEditingFBIndex(null); setNewFeature(''); setNewBenefit(''); setNewFBType('feature'); }}
                          >
                            Cancel
                          </Button>
                        )}
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>

                {/* List of features/benefits */}
                {(editingProduct.features_benefits || []).length === 0 ? (
                  <Alert variant="light" className="text-center">
                    No features added yet. Add your first feature above.
                  </Alert>
                ) : (
                  <Table striped bordered hover size="sm">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th style={{ width: '80px' }}>Type</th>
                        <th style={{ width: '90px' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {(editingProduct.features_benefits || []).map((fb, index) => (
                        <tr key={index} style={editingFBIndex === index ? { backgroundColor: '#fff8e7' } : {}}>
                          <td>{fb.feature || fb.benefit}</td>
                          <td>
                            <span className={`badge ${(fb.type || 'feature') === 'benefit' ? 'bg-info' : 'bg-dark'}`}
                              style={(fb.type || 'feature') === 'benefit' ? {} : { backgroundColor: colors.greyDarkest }}>
                              {(fb.type || 'feature') === 'benefit' ? 'Benefit' : 'Feature'}
                            </span>
                          </td>
                          <td className="text-center">
                            <Button
                              variant="link"
                              size="sm"
                              className="p-0 me-2"
                              style={{ color: colors.goldDark }}
                              onClick={() => startEditFeatureBenefit(index)}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="link"
                              size="sm"
                              className="text-danger p-0"
                              onClick={() => removeFeatureBenefit(index)}
                            >
                              X
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Tab>
            </Tabs>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button
            variant="dark"
            style={{ backgroundColor: colors.greyDarkest }}
            onClick={saveProduct}
            disabled={saving}
          >
            {saving ? 'Saving...' : (isAddMode ? 'Add Product' : 'Save Changes')}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default CCProductCatalog;
